import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Row, Col, Input, Button, Typography, Space, Tag, Tabs, Table,
  Statistic, Select, Switch, Form, message, Divider, Alert, List, Avatar,
  InputNumber, Slider, Tooltip, Badge, Spin
} from 'antd';
import {
  SendOutlined, RobotOutlined, ThunderboltOutlined,
  SettingOutlined, SearchOutlined, RocketOutlined, MessageOutlined,
  CheckCircleOutlined, FireOutlined, FilterOutlined, SaveOutlined,
  BankOutlined, LineChartOutlined, PlayCircleOutlined, ReloadOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// Em produção, usar URL relativa (nginx faz proxy)
const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8002';

// ============ TIPOS ============
interface Lead {
  id: number;
  cnpj: string;
  razao_social: string;
  produto: string;
  score: number;
  valor_divida: number;
  fase: string;
  uf: string;
  segmento: string;
  qtd_inscricoes?: number;
  ajuizada?: boolean;
}

interface Mensagem {
  id: number;
  direcao: 'INBOUND' | 'OUTBOUND';
  conteudo: string;
  timestamp: string;
  modelo?: string;
  reasoning?: string;
  analise?: {
    intencao: string;
    objecao_codigo?: string;
    sentimento: string;
  };
}

interface ConfigIA {
  modelo: string;
  tom: string;
  maxTokens: number;
  autoSalvar: boolean;
  systemPrompt: string;
}

// ============ CONSTANTES ============
const MODELOS = [
  { value: 'gpt-5.2', label: '🔥 GPT-5.2', desc: 'Mais avançado (recomendado)', preco: '$1.75/1M' },
  { value: 'gpt-5', label: '⭐ GPT-5', desc: 'Melhor qualidade', preco: '$1.00/1M' },
  { value: 'gpt-5-mini', label: '🚀 GPT-5 Mini', desc: 'Rápido e econômico', preco: '$0.25/1M' },
  { value: 'gpt-4.1-mini', label: '💰 GPT-4.1 Mini', desc: 'Básico', preco: '$0.80/1M' },
];

const TONS = [
  { value: 'profissional', label: 'Profissional', emoji: '👔' },
  { value: 'amigavel', label: 'Amigável', emoji: '😊' },
  { value: 'urgente', label: 'Urgente', emoji: '⚡' },
  { value: 'consultivo', label: 'Consultivo', emoji: '🎯' },
];

const SEGMENTOS = [
  { id: 'comercio', nome: 'Comércio', icon: '🏪', cor: '#1890ff' },
  { id: 'servicos', nome: 'Serviços', icon: '💼', cor: '#52c41a' },
  { id: 'industria', nome: 'Indústria', icon: '🏭', cor: '#722ed1' },
  { id: 'construcao', nome: 'Construção', icon: '🏗️', cor: '#fa8c16' },
  { id: 'transporte', nome: 'Transporte', icon: '🚚', cor: '#eb2f96' },
  { id: 'alimentacao', nome: 'Alimentação', icon: '🍽️', cor: '#13c2c2' },
];

const UFS = ['SP', 'RJ', 'MG', 'RS', 'PR', 'SC', 'BA', 'PE', 'CE', 'GO', 'DF'];

const formatCurrency = (v: number) => {
  if (v >= 1000000) {
    return `R$ ${(v/1000000).toFixed(1).replace('.', ',')} mi`;
  }
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v);
};

// ============ COMPONENTE PRINCIPAL ============
const IAProspeccaoPage: React.FC = () => {
  // Estados principais
  const [activeTab, setActiveTab] = useState('disparador');
  const [loading, setLoading] = useState(false);
  
  // Leads e seleção
  const [leads, setLeads] = useState<Lead[]>([]);
  const [selectedLeads, setSelectedLeads] = useState<number[]>([]);
  const [leadAtivo, setLeadAtivo] = useState<Lead | null>(null);
  
  // Chat
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [inputMsg, setInputMsg] = useState('');
  const [sessaoId, setSessaoId] = useState<string>('');
  const [triagemIdx, setTriagemIdx] = useState<number>(0);
  const [expandedReasoning, setExpandedReasoning] = useState<number[]>([]);
  const chatRef = useRef<HTMLDivElement>(null);
  
  // Configurações
  const [config, setConfig] = useState<ConfigIA>(() => {
    const saved = localStorage.getItem('sdr_config');
    return saved ? JSON.parse(saved) : {
      modelo: 'gpt-5.2',
      tom: 'profissional',
      maxTokens: 500,
      autoSalvar: true,
      systemPrompt: ''
    };
  });
  
  // Filtros
  const [segmentoAtivo, setSegmentoAtivo] = useState<string | null>(null);
  const [filtros, setFiltros] = useState({
    uf: [] as string[],
    valor_min: 50000,
    valor_max: 500000
  });

  // Salvar config automaticamente
  useEffect(() => {
    if (config.autoSalvar) {
      localStorage.setItem('sdr_config', JSON.stringify(config));
    }
  }, [config]);

  // Scroll chat
  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  }, [mensagens]);

  // ============ FUNÇÕES ============
  const buscarLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '15');
      if (segmentoAtivo) params.append('segmento', segmentoAtivo);
      if (filtros.uf.length > 0) params.append('uf', filtros.uf.join(','));
      params.append('valor_min', filtros.valor_min.toString());
      params.append('valor_max', filtros.valor_max.toString());

      const response = await axios.get(`${API_BASE}/api/v1/sdr/buscar-empresas?${params}`);
      
      if (response.data?.empresas) {
        const leadsData = response.data.empresas.map((e: any, idx: number) => ({
          id: idx + 1,
          cnpj: e.cnpj,
          razao_social: e.razao_social,
          produto: e.produto || 'RADAR',
          score: e.score || 70,
          valor_divida: e.valor_divida || e.valor_total || 0,
          fase: 'NOVO',
          uf: e.uf || 'SP',
          segmento: e.segmento || segmentoAtivo || 'comercio',
          qtd_inscricoes: e.qtd_inscricoes || 1,
          ajuizada: e.ajuizada || false
        }));
        setLeads(leadsData);
        message.success(`${leadsData.length} leads encontrados!`);
      }
    } catch (error) {
      console.error('Erro ao buscar leads:', error);
      message.error('Erro ao buscar leads. Verifique a conexão com a API.');
      setLeads([]);
    }
    setLoading(false);
  };

  const dispararCampanha = async () => {
    if (selectedLeads.length === 0) {
      message.warning('Selecione pelo menos um lead');
      return;
    }
    setLoading(true);
    const leadsParaDisparar = leads.filter(l => selectedLeads.includes(l.id));
    
    // Simular disparo
    await new Promise(r => setTimeout(r, 1500));
    
    message.success(`Campanha disparada para ${leadsParaDisparar.length} leads!`);
    setSelectedLeads([]);
    setLoading(false);
  };

  const enviarMensagem = async () => {
    if (!inputMsg.trim() || !leadAtivo) return;
    
    const novaMsgUser: Mensagem = {
      id: Date.now(),
      direcao: 'INBOUND',
      conteudo: inputMsg,
      timestamp: new Date().toISOString()
    };
    setMensagens(prev => [...prev, novaMsgUser]);
    setInputMsg('');
    setLoading(true);

    try {
      // Converter mensagens para formato de histórico
      const historicoChat = mensagens.map(m => ({
        role: m.direcao === 'OUTBOUND' ? 'assistant' : 'user',
        content: m.conteudo
      }));

      const response = await axios.post(`${API_BASE}/api/v1/sdr/chat`, {
        cnpj: leadAtivo.cnpj,
        mensagem: inputMsg,
        simular: true,
        modelo: config.modelo,
        tom: config.tom,
        system_prompt: config.systemPrompt || null,
        triagem_idx: triagemIdx,  // Passar índice atual
        historico: historicoChat,  // Passar histórico completo
        dados_lead: {
          razao_social: leadAtivo.razao_social,
          cnpj: leadAtivo.cnpj,
          valor_total: leadAtivo.valor_divida,
          produto: leadAtivo.produto,
        }
      });

      // Atualizar índice de triagem
      if (response.data?.triagem_idx !== undefined) {
        setTriagemIdx(response.data.triagem_idx);
      }

      const msgIA: Mensagem = {
        id: Date.now() + 1,
        direcao: 'OUTBOUND',
        conteudo: response.data?.resposta || 'Resposta da IA',
        timestamp: new Date().toISOString(),
        modelo: response.data?.metadata?.modelo || config.modelo,
        reasoning: response.data?.metadata?.reasoning || null,
        analise: response.data?.analise
      };
      const novasMsgs = [...mensagens, novaMsgUser, msgIA];
      setMensagens(novasMsgs);
      // Salvar sessão automaticamente
      if (leadAtivo) salvarSessao(novasMsgs, leadAtivo, response.data?.analise?.proxima_fase || 'TRIAGEM');
    } catch (error: any) {
      console.error('Erro no chat:', error);
      message.error(`Erro: ${error.response?.data?.detail || error.message || 'API offline'}`);
      // Mostrar erro na conversa
      const msgErro: Mensagem = {
        id: Date.now() + 1,
        direcao: 'OUTBOUND',
        conteudo: `⚠️ Erro ao conectar com IA: ${error.message || 'Verifique se o backend está rodando'}`,
        timestamp: new Date().toISOString(),
        modelo: 'erro'
      };
      setMensagens(prev => [...prev, msgErro]);
    }
    setLoading(false);
  };

  const salvarSessao = async (msgs: Mensagem[], lead: Lead, fase: string = 'TRIAGEM') => {
    if (!lead || msgs.length === 0) return;
    try {
      await axios.post(`${API_BASE}/api/v1/sdr/sessoes/salvar`, {
        sessao_id: sessaoId || undefined,
        cnpj: lead.cnpj,
        razao_social: lead.razao_social,
        valor_divida: lead.valor_divida,
        num_titulos: lead.qtd_inscricoes || 1,
        produto: lead.produto,
        modelo: config.modelo,
        tom: config.tom,
        mensagens: msgs.map(m => ({
          direcao: m.direcao,
          conteudo: m.conteudo,
          timestamp: m.timestamp,
          modelo: m.modelo
        })),
        fase_final: fase
      }).then(res => {
        if (res.data?.sessao_id && !sessaoId) {
          setSessaoId(res.data.sessao_id);
        }
      });
    } catch (e) {
      console.log('Erro ao salvar sessão:', e);
    }
  };

  const salvarConfig = () => {
    localStorage.setItem('sdr_config', JSON.stringify(config));
    message.success('Configurações salvas!');
  };

  const trocarModelo = async (modelo: string) => {
    setConfig(prev => ({ ...prev, modelo }));
    try {
      await axios.post(`${API_BASE}/api/v1/sdr/set-model?model_name=${modelo}`);
      message.success(`Modelo alterado para ${modelo}`);
    } catch {
      message.info(`Modelo configurado: ${modelo}`);
    }
  };

  // Iniciar conversa quando seleciona um lead (IA envia primeira mensagem)
  const iniciarConversa = async (lead: Lead) => {
    setLeadAtivo(lead);
    setMensagens([]);
    setSessaoId(''); // Nova sessão para cada lead
    setTriagemIdx(0); // Resetar índice de triagem
    setLoading(true);
    
    try {
      const response = await axios.post(`${API_BASE}/api/v1/sdr/iniciar-conversa`, {
        cnpj: lead.cnpj,
        modelo: config.modelo,
        tom: config.tom,
        system_prompt: config.systemPrompt || null,
        dados_lead: {
          razao_social: lead.razao_social,
          cnpj: lead.cnpj,
          valor_total: lead.valor_divida,
          num_titulos: lead.qtd_inscricoes || 1,
          produto: lead.produto,
          ajuizada: lead.ajuizada || false,
        }
      });

      if (response.data?.mensagem_abertura) {
        const msgAbertura: Mensagem = {
          id: Date.now(),
          direcao: 'OUTBOUND',
          conteudo: response.data.mensagem_abertura,
          timestamp: new Date().toISOString(),
          modelo: response.data?.metadata?.modelo || config.modelo
        };
        setMensagens([msgAbertura]);
        message.success('IA iniciou a conversa!');
      }
    } catch (error: any) {
      console.error('Erro ao iniciar conversa:', error);
      // Fallback: mensagem de abertura simples
      const msgFallback: Mensagem = {
        id: Date.now(),
        direcao: 'OUTBOUND',
        conteudo: `Olá! Aqui é da Save Co. Estou entrando em contato sobre a ${lead.razao_social}. Identificamos uma pendência de ${formatCurrency(lead.valor_divida)} que pode ser regularizada com condições especiais. Posso explicar como funciona?`,
        timestamp: new Date().toISOString(),
        modelo: config.modelo
      };
      setMensagens([msgFallback]);
    }
    setLoading(false);
  };

  // ============ COLUNAS DA TABELA ============
  const colunas = [
    {
      title: 'Empresa',
      dataIndex: 'razao_social',
      key: 'razao_social',
      render: (text: string, record: Lead) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 13 }}>{text.substring(0, 30)}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{record.cnpj}</Text>
        </Space>
      )
    },
    {
      title: 'UF',
      dataIndex: 'uf',
      key: 'uf',
      width: 60,
      render: (uf: string) => <Tag>{uf}</Tag>
    },
    {
      title: 'Valor',
      dataIndex: 'valor_divida',
      key: 'valor_divida',
      width: 100,
      render: (v: number) => <Text strong style={{ color: v > 200000 ? '#f5222d' : '#52c41a' }}>{formatCurrency(v)}</Text>
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      width: 80,
      render: (score: number) => (
        <Tag color={score >= 85 ? 'green' : score >= 70 ? 'blue' : 'orange'}>{score}</Tag>
      )
    },
    {
      title: 'Ação',
      key: 'acao',
      width: 120,
      render: (_: any, record: Lead) => (
        <Button 
          type="primary" 
          size="small" 
          icon={<MessageOutlined />}
          loading={loading && leadAtivo?.id === record.id}
          onClick={() => {
            iniciarConversa(record);
            setActiveTab('chat');
          }}
        >
          Prospectar
        </Button>
      )
    }
  ];

  // ============ RENDER ============
  return (
    <div style={{ padding: 24, background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <RobotOutlined style={{ fontSize: 28, color: '#1890ff' }} />
              <div>
                <Title level={4} style={{ margin: 0 }}>SDR Agent</Title>
                <Text type="secondary">Save Co. - Prospecção Inteligente</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space size="large">
              <Statistic title="Leads" value={leads.length} prefix={<BankOutlined />} />
              <Statistic title="Selecionados" value={selectedLeads.length} valueStyle={{ color: '#1890ff' }} />
              <Tag color="green" style={{ padding: '4px 12px' }}>
                <RobotOutlined /> {config.modelo}
              </Tag>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tabs principais */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: 'disparador',
            label: <span><RocketOutlined /> Disparador</span>,
            children: (
              <Row gutter={16}>
                {/* Painel de Segmentos */}
                <Col span={6}>
                  <Card title={<><FilterOutlined /> Segmentos</>} size="small">
                    <Space direction="vertical" style={{ width: '100%' }}>
                      {SEGMENTOS.map(seg => (
                        <Card
                          key={seg.id}
                          size="small"
                          hoverable
                          onClick={() => setSegmentoAtivo(segmentoAtivo === seg.id ? null : seg.id)}
                          style={{
                            cursor: 'pointer',
                            border: segmentoAtivo === seg.id ? `2px solid ${seg.cor}` : '1px solid #d9d9d9',
                            background: segmentoAtivo === seg.id ? `${seg.cor}10` : 'white'
                          }}
                        >
                          <Space>
                            <span style={{ fontSize: 20 }}>{seg.icon}</span>
                            <Text strong>{seg.nome}</Text>
                          </Space>
                        </Card>
                      ))}
                    </Space>

                    <Divider>Filtros</Divider>

                    <Form layout="vertical" size="small">
                      <Form.Item label="Estados (UF)">
                        <Select
                          mode="multiple"
                          placeholder="Todos"
                          value={filtros.uf}
                          onChange={(v) => setFiltros(prev => ({ ...prev, uf: v }))}
                          style={{ width: '100%' }}
                          maxTagCount={2}
                        >
                          {UFS.map(uf => <Option key={uf} value={uf}>{uf}</Option>)}
                        </Select>
                      </Form.Item>

                      <Form.Item label={`Valor: ${formatCurrency(filtros.valor_min)} - ${formatCurrency(filtros.valor_max)}`}>
                        <Slider
                          range
                          min={0}
                          max={1000000}
                          step={10000}
                          value={[filtros.valor_min, filtros.valor_max]}
                          onChange={(v) => setFiltros(prev => ({ ...prev, valor_min: v[0], valor_max: v[1] }))}
                          tooltip={{ formatter: (v) => formatCurrency(v || 0) }}
                        />
                      </Form.Item>

                      <Button 
                        type="primary" 
                        icon={<SearchOutlined />} 
                        block 
                        onClick={buscarLeads}
                        loading={loading}
                      >
                        Buscar Leads
                      </Button>
                    </Form>
                  </Card>
                </Col>

                {/* Lista de Leads */}
                <Col span={18}>
                  <Card 
                    title={<><BankOutlined /> Leads Encontrados ({leads.length})</>}
                    size="small"
                    extra={
                      <Space>
                        <Button 
                          icon={<ReloadOutlined />} 
                          onClick={buscarLeads}
                          loading={loading}
                        >
                          Atualizar
                        </Button>
                        <Button 
                          type="primary" 
                          icon={<ThunderboltOutlined />}
                          onClick={dispararCampanha}
                          disabled={selectedLeads.length === 0}
                          loading={loading}
                        >
                          Disparar ({selectedLeads.length})
                        </Button>
                      </Space>
                    }
                  >
                    <Table
                      dataSource={leads}
                      columns={colunas}
                      rowKey="id"
                      size="small"
                      pagination={{ pageSize: 10 }}
                      rowSelection={{
                        selectedRowKeys: selectedLeads,
                        onChange: (keys) => setSelectedLeads(keys as number[])
                      }}
                      loading={loading}
                    />
                  </Card>
                </Col>
              </Row>
            )
          },
          {
            key: 'chat',
            label: <span><MessageOutlined /> Chat Simulador</span>,
            children: (
              <Row gutter={16}>
                {/* Lista de leads para chat */}
                <Col span={8}>
                  <Card title="Leads para Conversar" size="small" style={{ height: 500, overflow: 'auto' }}>
                    {leads.length === 0 ? (
                      <Alert message="Busque leads primeiro na aba Disparador" type="info" />
                    ) : (
                      <List
                        dataSource={leads}
                        renderItem={(lead) => (
                          <List.Item
                            onClick={() => {
                              iniciarConversa(lead);
                            }}
                            style={{
                              cursor: 'pointer',
                              background: leadAtivo?.id === lead.id ? '#e6f7ff' : 'white',
                              padding: '8px 12px',
                              borderRadius: 4,
                              marginBottom: 4
                            }}
                          >
                            <List.Item.Meta
                              avatar={<Avatar style={{ background: '#1890ff' }}>{lead.razao_social[0]}</Avatar>}
                              title={<Text strong style={{ fontSize: 12 }}>{lead.razao_social.substring(0, 25)}</Text>}
                              description={
                                <Space size={4}>
                                  <Tag>{lead.uf}</Tag>
                                  <Text type="secondary" style={{ fontSize: 11 }}>{formatCurrency(lead.valor_divida)}</Text>
                                </Space>
                              }
                            />
                          </List.Item>
                        )}
                      />
                    )}
                  </Card>
                </Col>

                {/* Área do Chat */}
                <Col span={16}>
                  <Card 
                    title={
                      leadAtivo ? (
                        <Space>
                          <Avatar style={{ background: '#52c41a' }}>{leadAtivo.razao_social[0]}</Avatar>
                          <div>
                            <Text strong>{leadAtivo.razao_social}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {leadAtivo.cnpj} • {formatCurrency(leadAtivo.valor_divida)}
                            </Text>
                          </div>
                        </Space>
                      ) : 'Selecione um lead'
                    }
                    size="small"
                    style={{ height: 500 }}
                    extra={
                      leadAtivo && (
                        <Tag color="blue">
                          <RobotOutlined /> {config.modelo}
                        </Tag>
                      )
                    }
                  >
                    {/* Mensagens */}
                    <div
                      ref={chatRef}
                      style={{
                        height: 350,
                        overflowY: 'auto',
                        marginBottom: 16,
                        padding: 8,
                        background: '#fafafa',
                        borderRadius: 8
                      }}
                    >
                      {!leadAtivo ? (
                        <div style={{ textAlign: 'center', padding: 40 }}>
                          <RobotOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />
                          <br />
                          <Text type="secondary">Selecione um lead para iniciar o chat</Text>
                        </div>
                      ) : mensagens.length === 0 ? (
                        <Alert
                          message="Aguardando prospecção"
                          description="Clique em 'Prospectar' em um lead. A IA enviará a mensagem de abertura."
                          type="info"
                          showIcon
                        />
                      ) : (
                        mensagens.map(msg => (
                          <div
                            key={msg.id}
                            style={{
                              display: 'flex',
                              justifyContent: msg.direcao === 'OUTBOUND' ? 'flex-start' : 'flex-end',
                              marginBottom: 12
                            }}
                          >
                            <div
                              style={{
                                maxWidth: '75%',
                                padding: '10px 14px',
                                borderRadius: 12,
                                background: msg.direcao === 'OUTBOUND' ? '#1890ff' : '#f0f0f0',
                                color: msg.direcao === 'OUTBOUND' ? 'white' : 'black'
                              }}
                            >
                              <div style={{ marginBottom: 4 }}>
                                <Text style={{ fontSize: 11, color: msg.direcao === 'OUTBOUND' ? '#e6f7ff' : '#8c8c8c' }}>
                                  {msg.direcao === 'OUTBOUND' ? `🤖 IA (${msg.modelo || config.modelo})` : '👤 Lead'}
                                </Text>
                              </div>
                              <div>{msg.conteudo}</div>
                              
                              {/* Reasoning com animação estilo Gemini */}
                              {msg.reasoning && msg.direcao === 'OUTBOUND' && (
                                <div style={{ marginTop: 10 }}>
                                  <div 
                                    onClick={() => setExpandedReasoning(prev => 
                                      prev.includes(msg.id) 
                                        ? prev.filter(id => id !== msg.id) 
                                        : [...prev, msg.id]
                                    )}
                                    style={{ 
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6,
                                      padding: '6px 10px',
                                      background: 'rgba(255,255,255,0.15)',
                                      borderRadius: 8,
                                      transition: 'all 0.3s ease'
                                    }}
                                  >
                                    <span style={{ 
                                      fontSize: 14,
                                      transition: 'transform 0.3s ease',
                                      transform: expandedReasoning.includes(msg.id) ? 'rotate(90deg)' : 'rotate(0deg)'
                                    }}>
                                      💭
                                    </span>
                                    <span style={{ fontSize: 11, opacity: 0.9 }}>
                                      {expandedReasoning.includes(msg.id) ? 'Ocultar raciocínio' : 'Ver raciocínio da IA'}
                                    </span>
                                    <span style={{ 
                                      marginLeft: 'auto',
                                      fontSize: 10,
                                      transition: 'transform 0.3s ease',
                                      transform: expandedReasoning.includes(msg.id) ? 'rotate(180deg)' : 'rotate(0deg)'
                                    }}>
                                      ▼
                                    </span>
                                  </div>
                                  
                                  <div style={{
                                    maxHeight: expandedReasoning.includes(msg.id) ? '200px' : '0px',
                                    overflow: 'hidden',
                                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    opacity: expandedReasoning.includes(msg.id) ? 1 : 0,
                                  }}>
                                    <div style={{
                                      marginTop: 8,
                                      padding: '10px 12px',
                                      background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                                      borderRadius: 8,
                                      borderLeft: '3px solid rgba(255,255,255,0.5)',
                                      fontSize: 12,
                                      lineHeight: 1.5,
                                      fontStyle: 'italic'
                                    }}>
                                      <div style={{ marginBottom: 4, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                                        🧠 Cadeia de Pensamentos
                                      </div>
                                      {msg.reasoning}
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {msg.analise && (
                                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.3)' }}>
                                  <Space size={4}>
                                    <Tag style={{ fontSize: 10 }}>{msg.analise.intencao}</Tag>
                                    {msg.analise.objecao_codigo && (
                                      <Tag color="orange" style={{ fontSize: 10 }}>{msg.analise.objecao_codigo}</Tag>
                                    )}
                                  </Space>
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      )}
                      {loading && (
                        <div style={{ textAlign: 'center', padding: 16 }}>
                          <Spin tip="IA pensando..." />
                        </div>
                      )}
                    </div>

                    {/* Input */}
                    <Space.Compact style={{ width: '100%' }}>
                      <Input
                        placeholder={leadAtivo ? "Simule a resposta do lead (ex: 'não tenho interesse', 'quanto custa?')" : "Clique em Prospectar primeiro"}
                        value={inputMsg}
                        onChange={(e) => setInputMsg(e.target.value)}
                        onPressEnter={enviarMensagem}
                        disabled={!leadAtivo || loading}
                      />
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        onClick={enviarMensagem}
                        disabled={!leadAtivo || loading || !inputMsg.trim()}
                      >
                        Enviar
                      </Button>
                    </Space.Compact>
                  </Card>
                </Col>
              </Row>
            )
          },
          {
            key: 'config',
            label: <span><SettingOutlined /> Configurações</span>,
            children: (
              <Row gutter={24}>
                <Col span={12}>
                  <Card title={<><RobotOutlined /> Modelo de IA</>} size="small">
                    <Form layout="vertical">
                      <Form.Item label="Escolha o modelo">
                        <Select
                          value={config.modelo}
                          onChange={trocarModelo}
                          style={{ width: '100%' }}
                          size="large"
                        >
                          {MODELOS.map(m => (
                            <Option key={m.value} value={m.value}>
                              <Space>
                                <span>{m.label}</span>
                                <Text type="secondary" style={{ fontSize: 11 }}>- {m.desc}</Text>
                                <Tag color="green">{m.preco}</Tag>
                              </Space>
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      <Alert
                        message={`Modelo atual: ${config.modelo}`}
                        description={MODELOS.find(m => m.value === config.modelo)?.desc}
                        type="info"
                        showIcon
                        icon={<RobotOutlined />}
                      />
                    </Form>
                  </Card>

                  <Card title="Tom da Conversa" size="small" style={{ marginTop: 16 }}>
                    <Space wrap>
                      {TONS.map(tom => (
                        <Button
                          key={tom.value}
                          type={config.tom === tom.value ? 'primary' : 'default'}
                          onClick={() => setConfig(prev => ({ ...prev, tom: tom.value }))}
                          size="large"
                        >
                          {tom.emoji} {tom.label}
                        </Button>
                      ))}
                    </Space>
                  </Card>
                </Col>

                <Col span={12}>
                  <Card title="Prompt Customizado" size="small">
                    <Form layout="vertical">
                      <Form.Item label="System Prompt (instruções para a IA)">
                        <TextArea
                          rows={6}
                          placeholder="Deixe vazio para usar o prompt padrão. Exemplo: Você é um SDR especializado em dívidas tributárias..."
                          value={config.systemPrompt}
                          onChange={(e) => setConfig(prev => ({ ...prev, systemPrompt: e.target.value }))}
                        />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Define como a IA deve se comportar. Deixe vazio para usar o padrão.
                        </Text>
                      </Form.Item>

                      <Form.Item label={`Máximo de tokens: ${config.maxTokens}`}>
                        <Slider
                          min={100}
                          max={1000}
                          step={50}
                          value={config.maxTokens}
                          onChange={(v) => setConfig(prev => ({ ...prev, maxTokens: v }))}
                          marks={{ 100: 'Curto', 500: 'Médio', 1000: 'Longo' }}
                        />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          Controla o tamanho máximo das respostas da IA.
                        </Text>
                      </Form.Item>

                      <Form.Item label="Salvar automaticamente">
                        <Switch
                          checked={config.autoSalvar}
                          onChange={(v) => setConfig(prev => ({ ...prev, autoSalvar: v }))}
                        />
                        <Text type="secondary" style={{ marginLeft: 8 }}>
                          Salva as configurações no navegador
                        </Text>
                      </Form.Item>
                    </Form>
                  </Card>

                  <Card size="small" style={{ marginTop: 16 }}>
                    <Space>
                      <Button 
                        type="primary" 
                        icon={<SaveOutlined />} 
                        onClick={salvarConfig}
                        size="large"
                      >
                        Salvar Configurações
                      </Button>
                      <Button 
                        onClick={() => {
                          setConfig({
                            modelo: 'gpt-5-mini',
                            tom: 'profissional',
                            maxTokens: 500,
                            autoSalvar: true,
                            systemPrompt: ''
                          });
                          message.info('Configurações restauradas');
                        }}
                      >
                        Restaurar Padrão
                      </Button>
                    </Space>
                  </Card>
                </Col>
              </Row>
            )
          },
          {
            key: 'analytics',
            label: <span><LineChartOutlined /> Analytics</span>,
            children: (
              <Row gutter={16}>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="Total de Leads"
                      value={leads.length}
                      prefix={<BankOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="Conversas Hoje"
                      value={mensagens.length}
                      prefix={<MessageOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="Modelo Atual"
                      value={config.modelo}
                      prefix={<RobotOutlined />}
                      valueStyle={{ fontSize: 16 }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title="Status"
                      value="Desenvolvimento"
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: '#52c41a', fontSize: 16 }}
                    />
                  </Card>
                </Col>

                <Col span={24} style={{ marginTop: 16 }}>
                  <Alert
                    message="Modo Desenvolvimento"
                    description="O sistema está em modo de desenvolvimento. Todas as funcionalidades estão disponíveis para testes internos. Configure o modelo e parâmetros na aba Configurações."
                    type="success"
                    showIcon
                    icon={<PlayCircleOutlined />}
                  />
                </Col>
              </Row>
            )
          }
        ]} />
      </Card>
    </div>
  );
};

export default IAProspeccaoPage;
